import { motion } from "framer-motion";
import { ArrowRight, Sparkles, TrendingUp, Users } from "lucide-react";
import { Button } from "../components/ui/button";

export default function HeroSection() {
  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-[90vh] flex items-center overflow-hidden"
      style={{ background: "linear-gradient(135deg, #F8FAFC 0%, #EEF2FF 50%, #F8FAFC 100%)" }}
    >
      {/* Subtle grid pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(circle, #4F46E5 1px, transparent 1px)',
        backgroundSize: '32px 32px'
      }} />

      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl pt-24 pb-16">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left text */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-1.5 mb-6 shadow-sm">
              <Sparkles size={14} className="text-[#52B788]" />
              <span className="text-xs font-medium text-slate-600 uppercase tracking-wider">
                Full AI Marketing Agency
              </span>
            </div>

            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] mb-6"
              style={{ fontFamily: 'Outfit, sans-serif', color: '#0F172A' }}
              data-testid="hero-title"
            >
              Il tuo ufficio marketing{" "}
              <span className="text-[#52B788]">in affitto</span>
            </h1>

            <p className="text-base md:text-lg leading-relaxed text-slate-600 mb-8 max-w-xl">
              Con Colibri Marketing Agency hai un team di professionisti pronti per le tue esigenze.
              Strategie digitali potenziate dall'intelligenza artificiale, a partire da{" "}
              <strong className="text-slate-800">249 euro/mese</strong> con sito web incluso.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => scrollTo("#contatti")}
                className="bg-[#1B4332] hover:bg-[#143728] text-white rounded-full h-12 px-8 font-medium shadow-lg shadow-green-900/20 text-base"
                data-testid="hero-cta-button"
              >
                Prenota una Call Gratuita
                <ArrowRight size={18} className="ml-2" />
              </Button>
              <Button
                onClick={() => scrollTo("#servizi")}
                variant="outline"
                className="border-slate-200 hover:bg-slate-50 rounded-full h-12 px-8 font-medium text-base"
                data-testid="hero-secondary-button"
              >
                Scopri i Servizi
              </Button>
            </div>

            {/* Mini stats */}
            <div className="flex items-center gap-8 mt-10 pt-8 border-t border-slate-200/60">
              <div>
                <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>150+</p>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Clienti Attivi</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>15+</p>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Anni Esperienza</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>98%</p>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Clienti Soddisfatti</p>
              </div>
            </div>
          </motion.div>

          {/* Right visual */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="relative hidden lg:block"
          >
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1610702876884-0f8473590287?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBvZmZpY2UlMjBkaWdpdGFsJTIwbWFya2V0aW5nJTIwdGVhbSUyMGNvbGxhYm9yYXRpb258ZW58MHx8fHwxNzczMzA1ODg0fDA&ixlib=rb-4.1.0&q=85&w=800"
                alt="Team di marketing digitale"
                className="rounded-2xl shadow-2xl shadow-slate-200/50 w-full object-cover aspect-[4/3]"
                data-testid="hero-image"
              />

              {/* Floating card - Growth */}
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl p-4 shadow-lg border border-slate-100 animate-float">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                    <TrendingUp size={20} className="text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Crescita Media</p>
                    <p className="text-sm font-bold text-slate-900">+340%</p>
                  </div>
                </div>
              </div>

              {/* Floating card - Clients */}
              <div className="absolute -top-4 -right-4 bg-white rounded-xl p-4 shadow-lg border border-slate-100 animate-float" style={{ animationDelay: '1s' }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                    <Users size={20} className="text-[#1B4332]" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Nuovi Lead</p>
                    <p className="text-sm font-bold text-slate-900">+85/mese</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
